import React from "react";
import { Link } from "react-router-dom";
import heroImage from "../assets/brand/hero.png";
import missionImage from "../assets/brand/mission.jpg";
import whyImage from "../assets/brand/why.jpg";
import skillImage from "../assets/brand/skill.png";
import blogThumb1 from "../assets/brand/66335c6170defd5939da04bede8011552c798ab7.jpg";
import blogThumb2 from "../assets/brand/f07140aec01faafd27493c3ad13ccbb09797792c.jpg";
import p1 from "../assets/brand/p1.svg";
import p2 from "../assets/brand/p2.svg";
import p3 from "../assets/brand/p3.svg";
import safeLockImage from "../assets/images/pre/safe-lock.jpg";
import baoafrikImage from "../assets/images/pre/baoafrik.png";
import Button from "../components/ui/Button";
import Badge from "../components/ui/Badge";
import Card from "../components/ui/Card";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { fetchBlogPosts } from "../features/blog/thunks/blog.thunk";
import { submitContactForm } from "../features/contact/thunks/contact.thunk";
import { resetContactState } from "../features/contact/slices/contact.slice";
import {
  validateContact,
  ContactErrors,
} from "../features/contact/utils/validateContact";

const BLOG_FALLBACK_IMAGES = [blogThumb1, blogThumb2] as const;

const WHY_BAO_ITEMS = [
  {
    title: "Practical solutions, not tech for its own sake.",
    description:
      "We don’t recommend technology because it’s trendy. We recommend it because it solves your problem.",
  },
  {
    title: "Built for the African market.",
    description:
      "Our solutions account for the realities of doing business here — connectivity, regulation, payment systems, and user behaviour — because we live them too.",
  },
  {
    title: "Transparent from day one.",
    description:
      "You’ll know what we’re building, why we’re building it, and when you can expect it. No surprises, no black boxes.",
  },
  {
    title: "Quality built in, not checked at the end.",
    description:
      "QA runs throughout the entire development process, not as a last-minute step before delivery.",
  },
  {
    title: "Flexible collaboration.",
    description:
      "We work as an extension of your team — whether that means daily stand-ups, weekly reviews, or sprint-based delivery.",
  },
  {
    title: "Long-term support.",
    description:
      "Our relationship doesn’t end at launch. We support your platform as it grows and evolves.",
  },
  {
    title: "Honest communication.",
    description:
      "We tell you what’s possible, what’s difficult, and what will take time — because trust is more important than a signed contract.",
  },
] as const;

const TESTIMONIAL_ITEMS = [
  {
    quote:
      "Lorem ipsum dolor sit amet consectetur. Amet mi porttitor duis facilisis amet erat urna. Lorem ipsum dolor sit amet consectetur.",
    name: "Herman Kabore",
    role: "CEO at Nwebs",
    avatarColor: "#D7ECFF",
  },
  {
    quote:
      "Lorem ipsum dolor sit amet consectetur. Amet mi porttitor duis facilisis amet erat urna. Lorem ipsum dolor sit amet consectetur.",
    name: "Herman Kabore",
    role: "CEO at Nwebs",
    avatarColor: "#FFE6D3",
  },
  {
    quote:
      "Lorem ipsum dolor sit amet consectetur. Amet mi porttitor duis facilisis amet erat urna. Lorem ipsum dolor sit amet consectetur.",
    name: "Herman Kabore",
    role: "CEO at Nwebs",
    avatarColor: "#E9F7E9",
  },
] as const;

const PRODUCTS = [
  {
    name: "BaoAfrik",
    description: "A digital marketplace for African products and services.",
    status: "Live product currently being validated and improved.",
    cta: "Explore BaoAfrik",
    href: "https://baoafrik.com/",
    external: true,
  },
  {
    name: "Port2Market",
    description: "A trade visibility platform for the port ecosystem.",
    status: "Product research and UI/UX design in progress.",
    cta: "Follow This Project",
    href: "/contact?subject=Follow%20Port2Market",
    external: false,
  },
  {
    name: "AfrikBooking",
    description: "A configurable appointment and service-management platform.",
    status: "Concept prototype under evaluation.",
    cta: "Interested? Let Us Know",
    href: "/contact?subject=Interested%20in%20AfrikBooking",
    external: false,
  },
] as const;

const stripHtml = (html: string) =>
  html
    .replace(/<[^>]*>?/gm, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

type RoleBadgeProps = {
  label: string;
  labelBg: string;
  labelTextColor: string;
  bubbleColor: string;
  side: "left" | "right";
  className?: string;
};

const RoleBadge: React.FC<RoleBadgeProps> = ({
  label,
  labelBg,
  labelTextColor,
  bubbleColor,
  side,
  className = "",
}) => {
  const isLeft = side === "left";

  return (
    <div className={`inline-flex items-center ${className}`}>
      {isLeft ? (
        <>
          <Badge
            className={`rounded-[6px] rounded-br-[1px] px-2 py-1 font-title text-[9px] font-medium`}
            style={{ backgroundColor: labelBg, color: labelTextColor }}
          >
            {label}
          </Badge>
          <span
            className="ml-1 h-6 w-6 rounded-[999px] rounded-br-[2px]"
            style={{ backgroundColor: bubbleColor }}
          />
        </>
      ) : (
        <>
          <span
            className="h-6 w-6 rounded-[999px] rounded-bl-[2px]"
            style={{ backgroundColor: bubbleColor }}
          />
          <Badge
            className="ml-1 rounded-[6px] rounded-bl-[1px] px-2 py-1 font-title text-[9px] font-medium"
            style={{ backgroundColor: labelBg, color: labelTextColor }}
          >
            {label}
          </Badge>
        </>
      )}
    </div>
  );
};

type FormFieldProps = {
  label: string;
  type?: "text" | "email";
  placeholder: string;
  multiline?: boolean;
  name: string;
  value: string;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  error?: string;
};

const FormField: React.FC<FormFieldProps> = ({
  label,
  type = "text",
  placeholder,
  multiline = false,
  name,
  value,
  onChange,
  error,
}) => (
  <div>
    <label className="mb-1 block font-title text-[10px] font-medium text-[#212121]">
      {label}
    </label>
    {multiline ? (
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        rows={4}
        placeholder={placeholder}
        className={`w-full rounded-[8px] border bg-white px-3 py-2 font-description text-[10px] font-light text-[#212121] placeholder:text-[#D9D9D9] focus:outline-none ${
          error ? "border-red-300" : "border-[#E4E4E4]"
        }`}
      />
    ) : (
      <input
        name={name}
        value={value}
        onChange={onChange}
        type={type}
        placeholder={placeholder}
        className={`h-9 w-full rounded-[8px] border bg-white px-3 font-description text-[10px] font-light text-[#212121] placeholder:text-[#D9D9D9] focus:outline-none ${
          error ? "border-red-300" : "border-[#E4E4E4]"
        }`}
      />
    )}
    {error && (
      <p className="mt-1 font-description text-[9px] text-red-500">{error}</p>
    )}
  </div>
);

const Home: React.FC = () => {
  const dispatch = useAppDispatch();
  const { posts, isLoading: isBlogLoading } = useAppSelector(
    (state) => state.blog,
  );
  const {
    isSubmitting,
    isSubmitted,
    error: contactError,
  } = useAppSelector((state) => state.contact);
  const [contactAudience, setContactAudience] = React.useState<
    "personal" | "company"
  >("personal");
  const [form, setForm] = React.useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = React.useState<ContactErrors>({});

  React.useEffect(() => {
    return () => {
      dispatch(resetContactState());
    };
  }, [dispatch]);

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name as keyof ContactErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validationErrors = validateContact(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    const result = await dispatch(
      submitContactForm({
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
      }),
    );
    if (submitContactForm.fulfilled.match(result)) {
      setForm({ name: "", email: "", subject: "", message: "" });
    }
  };

  React.useEffect(() => {
    dispatch(fetchBlogPosts(2));
  }, [dispatch]);

  const featuredArticles = React.useMemo(() => {
    return posts.slice(0, 2).map((post, index) => {
      const excerpt = stripHtml(post.excerptHtml || "").trim();
      return {
        slug: post.slug,
        image:
          post.featuredMediaUrl ||
          BLOG_FALLBACK_IMAGES[index % BLOG_FALLBACK_IMAGES.length],
        category: post.categoryNames?.[0] || "Article",
        date: new Date(post.date).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        }),
        author: post.authorName || "Bao Technologies",
        title: post.title,
        description: excerpt
          ? `${excerpt.slice(0, 140)}${excerpt.length > 140 ? "..." : ""}`
          : "",
      };
    });
  }, [posts]);

  const showBlogSection = !isBlogLoading && featuredArticles.length > 0;

  return (
    <div className="overflow-x-hidden">
      <section className="bg-white pt-6">
        <div className="mx-auto grid max-w-[1360px] grid-cols-2 items-center gap-1 px-5 pb-7 pt-0 max-[980px]:grid-cols-1 max-[980px]:gap-6">
          <div className="max-w-[620px] max-[980px]:max-w-full max-[980px]:text-center">
            <Badge className="inline-flex rounded-[8px] bg-[#E9F1FD] px-[10px] py-[3px] font-description text-[9px] font-medium text-[#2776EA]">
              Tailored SaaS and digital growth solutions
            </Badge>

            <h1 className="mt-3 font-title text-[46px] font-semibold leading-[1.08] tracking-[-0.02em] text-[#212121] max-[1200px]:text-[40px] max-[980px]:text-[54px] max-[700px]:text-[32px]">
              Technology Solutions That{" "}
              <span className="font-title font-semibold text-[#2776EA]">
                Move
              </span>{" "}
              Organisations{" "}
              <span className="font-title font-semibold text-[#2776EA]">
                Forward
              </span>
            </h1>

            <h2 className="mt-3 max-w-[520px] font-description text-[15px] font-light leading-[1.5] text-[#919191] max-[980px]:mx-auto max-[980px]:max-w-[700px] max-[980px]:text-[13px] max-[700px]:text-[13px]">
              We design and deliver practical digital solutions for businesses,
              public institutions and growing organisations across Africa.
            </h2>

            <div className="mt-4 flex items-center gap-3 max-[980px]:justify-center">
              <Link to="/contact">
                <Button className="rounded-[8px] bg-[#2776EA] px-4 py-[6px] font-description text-[11px] font-medium text-white">
                  Start a Conversation
                </Button>
              </Link>
              <Link to="/services">
                <Button className="rounded-[8px] border border-[#2776EA] bg-white px-6 py-[6px] font-description text-[11px] font-medium text-[#2776EA]">
                  Explore Our Services
                </Button>
              </Link>
            </div>

            {/* <div className="mt-12 flex items-start gap-12 max-[980px]:justify-center">
              <div>
                <p className="font-title text-[32px] font-semibold leading-none text-[#212121]">
                  +5
                </p>
                <p className="mt-2 font-description text-[15px] leading-[1.35] text-[#919191] uppercase tracking-wider">
                  Years of
                  <br />
                  experience
                </p>
              </div>
              <div>
                <p className="font-title text-[32px] font-semibold leading-none text-[#212121]">
                  +10
                </p>
                <p className="mt-2 font-description text-[15px] leading-[1.35] text-[#919191] uppercase tracking-wider">
                  Projects
                  <br />
                  completed
                </p>
              </div>
              <div>
                <p className="font-title text-[32px] font-semibold leading-none text-[#212121]">
                  +25
                </p>
                <p className="mt-2 font-description text-[15px] leading-[1.35] text-[#919191] uppercase tracking-wider">
                  Clients
                  <br />
                  Satisfied
                </p>
              </div>
            </div> */}
          </div>

          <div className="relative min-h-[430px] max-[980px]:min-h-[420px]">
            <img
              src={heroImage}
              alt="Blue toned hand visual"
              className="absolute right-[4%] top-1/2 w-[70%] -translate-y-1/2 object-contain max-[980px]:left-1/2 max-[980px]:right-auto max-[980px]:w-[64%] max-[980px]:-translate-x-1/2"
            />
            <div className="pointer-events-none absolute right-[8%] top-1/2 w-[54%] aspect-square -translate-y-1/2 max-[980px]:left-1/2 max-[980px]:right-auto max-[980px]:w-[52%] max-[980px]:-translate-x-1/2">
              <div className="absolute inset-0 rounded-full border border-[#E1E1E1]" />
              <div className="absolute left-1/2 top-1/2 h-[74%] w-[74%] -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#E1E1E1]" />
              <div className="absolute left-1/2 top-1/2 h-[48%] w-[48%] -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#E1E1E1]" />
            </div>

            <Badge className="absolute right-[11%] top-[12%] rounded-[6px] bg-[#0754C64D] px-[8px] py-[3px] font-description text-[11px] font-normal text-white [backdrop-filter:blur(2px)] shadow-[0_8px_38px_0_rgba(7,84,198,0.38)]">
              User centered product
            </Badge>
            <Badge className="absolute left-[29%] top-[31%] rounded-[6px] bg-[#0754C64D] px-[8px] py-[3px] font-description text-[11px] font-normal text-white [backdrop-filter:blur(2px)] shadow-[0_8px_38px_0_rgba(7,84,198,0.38)]">
              API Development
            </Badge>
            <Badge className="absolute right-[4%] top-[54%] rounded-[6px] bg-[#0754C64D] px-[8px] py-[3px] font-description text-[11px] font-normal text-white [backdrop-filter:blur(2px)] shadow-[0_8px_38px_0_rgba(7,84,198,0.38)]">
              Digital Marketing Services
            </Badge>
            <Badge className="absolute left-[29%] top-[69%] rounded-[6px] bg-[#0754C64D] px-[8px] py-[3px] font-description text-[11px] font-normal text-white [backdrop-filter:blur(2px)] shadow-[0_8px_38px_0_rgba(7,84,198,0.38)]">
              Web/Mobile Development
            </Badge>
            <Badge className="absolute right-[17%] top-[84%] rounded-[6px] bg-[#0754C64D] px-[8px] py-[3px] font-description text-[11px] font-normal text-white [backdrop-filter:blur(2px)] shadow-[0_8px_38px_0_rgba(7,84,198,0.38)]">
              B2B Saas Solutions
            </Badge>
          </div>
        </div>
      </section>

      <section className="bg-white pb-16 pt-16 md:pt-20">
        <div className="mx-auto max-w-[1360px] px-5">
          <div className="mx-auto max-w-[800px] text-center">
            <Badge className="inline-flex rounded-full border border-[#B8B8B8] bg-white px-3 py-1 font-description text-[10px] font-normal text-[#9C9C9C]">
              Our Purpose
            </Badge>
            <h2 className="mt-3 font-title text-[32px] font-semibold leading-[1.1] tracking-[-0.02em] text-[#212121] md:text-[40px] max-[700px]:text-[28px]">
              Technology That Solves Real Problems.
            </h2>
            <p className="mx-auto mt-4 max-w-[680px] font-description text-[13px] font-light leading-[1.55] text-[#919191] md:text-[14px]">
              At Bao Technologies, we believe technology should remove barriers,
              not create them. We started this company because we saw
              businesses, organisations and public institutions struggling with
              manual processes, disconnected systems and software that wasn’t
              designed for the realities of the African market.
            </p>
            <p className="mx-auto mt-4 max-w-[680px] font-description text-[13px] font-light leading-[1.55] text-[#919191] md:text-[14px]">
              Our mission is simple: build practical digital solutions that help
              organisations work more efficiently, serve people better and
              create lasting impact.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-white pb-10 pt-24">
        <div className="mx-auto max-w-[1360px] px-5">
          <div className="text-center">
            <Badge className="inline-flex rounded-full border border-[#B8B8B8] bg-white px-3 py-1 font-description text-[10px] font-normal text-[#9C9C9C]">
              Our Services
            </Badge>
            <h2 className="mt-3 font-title text-[42px] font-semibold leading-none text-[#212121]">
              Explore Our services
            </h2>
            <p className="mx-auto mt-2 max-w-[560px] font-description text-[12px] font-light leading-[1.4] text-[#919191]">
              From product engineering to market expansion, we build and scale
              digital solutions that help organizations serve users better and
              grow with confidence.
            </p>
          </div>

          <div className="mx-auto mt-8 grid max-w-[1280px] grid-cols-3 gap-2 max-[760px]:grid-cols-1">
            <Card className="flex min-h-[250px] flex-col rounded-[20px] bg-gradient-to-b from-[#2776EA] to-[#164384] px-3.5 pb-4 pt-5 max-[980px]:min-h-[210px] max-[980px]:px-3 max-[980px]:pt-4">
              <div className="flex items-center">
                <img
                  src={p1}
                  alt=""
                  aria-hidden="true"
                  className="h-9 w-9 rounded-full border-2 border-[#2776EA] bg-white"
                />
                <img
                  src={p2}
                  alt=""
                  aria-hidden="true"
                  className="-ml-2 h-9 w-9 rounded-full border-2 border-[#2776EA] bg-white"
                />
                <img
                  src={p3}
                  alt=""
                  aria-hidden="true"
                  className="-ml-2 h-9 w-9 rounded-full border-2 border-[#2776EA] bg-white"
                />
              </div>

              <p className="mt-2.5 max-w-[210px] font-description text-[10px] font-light leading-[1.35] text-[#E4E4E4]">
                We design and ship reliable web and mobile platforms focused on
                performance, usability, and scale.
              </p>

              <div className="mt-10 h-px bg-[#E4E4E4]/35" />

              <div className="mt-auto pt-2">
                <h3 className="font-title text-[24px] font-medium leading-[1.08] text-white">
                  Web/Mobile
                  <br />
                  Development
                </h3>
                <p className="mt-2 max-w-[260px] font-description text-[11px] font-light leading-[1.45] text-[#E4E4E4]">
                  From customer portals to operational dashboards, we create
                  mobile-ready software that fits your workflows and delivers
                  measurable impact.
                </p>
                <Link
                  to="/services#web-mobile"
                  className="mt-3 inline-flex items-center gap-2 rounded-full bg-white/20 px-5 py-[5px] font-title text-[10px] font-light text-white"
                >
                  Learn more
                  <span aria-hidden="true">ΓåÆ</span>
                </Link>
              </div>
            </Card>

            {/* <Card
              className="relative overflow-hidden rounded-[20px] px-3.5 pb-4 pt-5"
              style={{
                backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.88) 100%), url(${cardImage})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <div className="min-h-[250px] flex items-end max-[980px]:min-h-[210px]">
                <div>
                  <h3 className="font-title text-[26px] font-medium leading-[1.08] text-white">
                    B2B Saas Solutions
                    <br />
                    Around Your Operations
                  </h3>
                  <p className="mt-8 max-w-[270px] font-description text-[14px] font-light leading-[1.45] text-white">
                    We build tailored platforms that replace inefficient manual
                    processes, connect teams and make information easier to
                    manage.
                  </p>
                </div>
                <Link to="/services/software-development">
                  <Button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-full bg-[#434343] px-5 py-[5px] font-title text-[10px] font-light text-white"
                  >
                    Learn more
                    <span aria-hidden="true">ΓåÆ</span>
                  </Button>
                </Link>
              </div>
            </Card> */}

            <Card className="h-full rounded-[20px] bg-[#212121] px-4 pb-5 pt-5 md:px-5 md:pt-6">
              <div className="flex h-full min-h-[250px] flex-col items-start justify-between gap-4 md:min-h-[260px]">
                <div className="w-full">
                  <h3 className="font-title text-[20px] font-medium leading-[1.08] text-white">
                    Digital Experiences
                    <br />
                    That Help You Serve Customers Better
                  </h3>
                  <p className="mt-4 max-w-[270px] font-description text-[14px] font-light leading-[1.45] text-white">
                    We create responsive websites and applications that are
                    practical, accessible and designed around real user needs.
                  </p>
                </div>
                <Link to="/services#web-mobile">
                  <Button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-full bg-[#434343] px-5 py-[5px] font-title text-[10px] font-light text-white"
                  >
                    Learn more
                    <span aria-hidden="true">ΓåÆ</span>
                  </Button>
                </Link>
              </div>
            </Card>

            <Card className="h-full rounded-[20px] bg-gradient-to-b from-[#2776EA] to-[#164384] px-4 pb-5 pt-5 md:px-5 md:pt-6">
              <div className="flex h-full min-h-[250px] flex-col items-start justify-between gap-4 md:min-h-[260px]">
                <div className="w-full">
                  <h3 className="font-title text-[20px] font-medium leading-[1.08] text-white">
                    Release Software
                    <br />
                    With Greater Confidence
                  </h3>
                  <p className="mt-8 max-w-[270px] font-description text-[14px] font-light leading-[1.45] text-white">
                    Our testing services help identify defects, validate
                    critical journeys and improve product reliability before
                    users are affected.
                  </p>
                </div>
                <Link to="/services#quality-assurance">
                  <Button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-full bg-[#434343] px-5 py-[5px] font-title text-[10px] font-light text-white"
                  >
                    Learn more
                    <span aria-hidden="true">ΓåÆ</span>
                  </Button>
                </Link>
              </div>
            </Card>

            <Card className="flex min-h-[250px] flex-col rounded-[20px] bg-gradient-to-b from-[#2776EA] to-[#164384] px-3.5 pb-4 pt-5 max-[980px]:min-h-[210px] max-[980px]:px-3 max-[980px]:pt-4">
              <div className="flex items-center">
                <img
                  src={p1}
                  alt=""
                  aria-hidden="true"
                  className="h-9 w-9 rounded-full border-2 border-[#2776EA] bg-white"
                />
                <img
                  src={p2}
                  alt=""
                  aria-hidden="true"
                  className="-ml-2 h-9 w-9 rounded-full border-2 border-[#2776EA] bg-white"
                />
                <img
                  src={p3}
                  alt=""
                  aria-hidden="true"
                  className="-ml-2 h-9 w-9 rounded-full border-2 border-[#2776EA] bg-white"
                />
              </div>

              <p className="mt-2.5 max-w-[210px] font-description text-[10px] font-light leading-[1.35] text-[#E4E4E4]">
                Quality engineering that helps reduce risk, validate user
                journeys, and deliver trusted software.
              </p>

              <div className="mt-10 h-px bg-[#E4E4E4]/35" />

              <div className="mt-auto pt-2">
                <h3 className="font-title text-[24px] font-medium leading-[1.08] text-white">
                  Software QA
                  <br />
                  &amp; Delivery
                </h3>
                <p className="mt-2 max-w-[260px] font-description text-[11px] font-light leading-[1.45] text-[#E4E4E4]">
                  We test product-critical journeys early, improve reliability,
                  and give teams more confidence before release.
                </p>
                <Link
                  to="/services#qa"
                  className="mt-3 inline-flex items-center gap-2 rounded-full bg-white/20 px-5 py-[5px] font-title text-[10px] font-light text-white"
                >
                  Learn more
                  <span aria-hidden="true">→</span>
                </Link>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Innovation lab section */}
      <section className="bg-white pb-16 pt-20">
        <div className="mx-auto max-w-[1360px]">
          <div className="rounded-[30px] bg-[#F8FAFE] px-8 py-12 max-[760px]:px-5 max-[760px]:py-8">
            <div className="mx-auto max-w-[800px] text-center">
              <Badge className="inline-flex rounded-full border border-[#B8B8B8] bg-white px-3 py-1 font-description text-[10px] font-normal text-[#9C9C9C]">
                Innovation Lab
              </Badge>
              <h2 className="mt-4 font-title text-[36px] font-medium leading-[1.1] text-[#212121] max-[700px]:text-[28px]">
                We believe the best products begin with understanding real
                problems.
              </h2>
              <p className="mx-auto mt-5 max-w-[680px] font-description text-[16px] font-light leading-[1.6] text-[#919191]">
                Our Innovation Lab allows us to research challenges, validate
                ideas, and develop practical digital products before bringing
                them to market. Some ideas evolve into our own platforms, while
                others inspire solutions we build for clients.
              </p>
              <p className="mx-auto mt-4 max-w-[680px] font-description text-[16px] font-light leading-[1.6] text-[#919191]">
                This is what sets us apart from traditional software agencies —
                we don't just build what you ask for. We bring product thinking,
                real-world validation, and hands-on experience from building our
                own products to every client project.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Project section */}
      <section className="bg-white pb-16 pt-20">
        <div className="mx-auto max-w-[1360px] px-5">
          <div className="text-center">
            <Badge className="inline-flex rounded-full border border-[#B8B8B8] bg-white px-3 py-1 font-description text-[10px] font-normal text-[#9C9C9C]">
              Featured Project
            </Badge>
            <h2 className="mt-3 font-title text-[42px] font-semibold leading-none text-[#212121] max-[700px]:text-[32px]">
              Featured Project
            </h2>
          </div>

          <div className="mx-auto mt-8 max-w-[960px] rounded-[20px] border border-[#E4E4E4] bg-white px-8 py-8 shadow-[1px_2px_30px_0_rgba(0,0,0,0.04)] max-[760px]:px-5 max-[760px]:py-6">
            <div className="flex flex-col gap-6 md:flex-row md:items-start md:gap-10">
              <div className="flex aspect-[4/3] w-full shrink-0 items-center justify-center overflow-hidden rounded-[12px] bg-[#F8FAFE] md:w-[280px]">
                <img
                  src={safeLockImage}
                  alt="Safe Locks Storage Ltd website showcase"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="flex-1">
                <h3 className="font-title text-[24px] font-medium leading-tight text-[#212121] max-[700px]:text-[20px]">
                  Safe Locks Storage Ltd — United Kingdom
                </h3>
                <p className="mt-3 font-description text-[12px] font-medium uppercase tracking-wider text-[#919191]">
                  Industry: Self Storage
                </p>
                <div className="mt-4">
                  <p className="font-description text-[12px] font-medium text-[#616161]">
                    Services Delivered
                  </p>
                  <ul className="mt-2 flex flex-wrap gap-x-6 gap-y-1">
                    {[
                      "Website Design",
                      "Website Development",
                      "Responsive Design",
                      "Corporate Website",
                    ].map((s) => (
                      <li
                        key={s}
                        className="relative pl-4 font-description text-[13px] font-light text-[#919191] before:absolute before:left-0 before:top-[7px] before:h-[5px] before:w-[5px] before:rounded-full before:bg-[#2776EA]"
                      >
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="mt-4 font-description text-[14px] font-light leading-[1.6] text-[#616161]">
                  Designed and developed a modern, responsive website that
                  improves the company&apos;s online presence, showcases their
                  storage solutions, and makes it easy for customers to find and
                  contact them.
                </p>
              </div>
            </div>
          </div>

          <div className="mx-auto mt-8 max-w-[960px] rounded-[20px] border border-[#E4E4E4] bg-white px-8 py-8 shadow-[1px_2px_30px_0_rgba(0,0,0,0.04)] max-[760px]:px-5 max-[760px]:py-6">
            <div className="flex flex-col gap-6 md:flex-row md:items-start md:gap-10">
              <div className="flex aspect-[4/3] w-full shrink-0 items-center justify-center overflow-hidden rounded-[12px] bg-[#F8FAFE] md:w-[280px]">
                <img
                  src={baoafrikImage}
                  alt="BaoAfrik marketplace showcase"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="flex-1">
                <h3 className="font-title text-[24px] font-medium leading-tight text-[#212121] max-[700px]:text-[20px]">
                  BaoAfrik — Digital Marketplace
                </h3>
                <p className="mt-3 font-description text-[12px] font-medium uppercase tracking-wider text-[#919191]">
                  Industry: E-commerce & Marketplace
                </p>
                <div className="mt-4">
                  <p className="font-description text-[12px] font-medium text-[#616161]">
                    Services Delivered
                  </p>
                  <ul className="mt-2 flex flex-wrap gap-x-6 gap-y-1">
                    {[
                      "Brand Positioning",
                      "Product Design",
                      "Website Development",
                      "Marketplace UX",
                      "Responsive Experience",
                    ].map((s) => (
                      <li
                        key={s}
                        className="relative pl-4 font-description text-[13px] font-light text-[#919191] before:absolute before:left-0 before:top-[7px] before:h-[5px] before:w-[5px] before:rounded-full before:bg-[#2776EA]"
                      >
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="mt-4 font-description text-[14px] font-light leading-[1.6] text-[#616161]">
                  BaoAfrik is a digital marketplace designed to help African
                  businesses and customers discover products and services in a
                  more connected, convenient, and trusted way.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white pb-16 pt-20">
        <div className="mx-auto max-w-[1360px] px-5">
          <div className="max-w-[800px]">
            <Badge className="inline-flex rounded-full border border-[#B8B8B8] bg-white px-3 py-1 font-description text-[10px] font-normal text-[#9C9C9C]">
              How We Deliver
            </Badge>
            <h2 className="mt-4 font-title text-[36px] font-medium leading-[1.1] text-[#212121] max-[700px]:text-[28px]">
              How We Deliver
            </h2>
            <p className="mt-3 max-w-[680px] font-description text-[14px] font-light leading-[1.6] text-[#919191]">
              We don't disappear for months and come back with something you
              didn't ask for. Our process is structured to keep you involved at
              every stage, with regular communication, collaborative reviews,
              and continuous feedback loops.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-4 max-[760px]:gap-3">
            {[
              {
                step: "01",
                title: "Discovery & Problem Understanding",
                desc: "We start with discovery workshops to learn about your organisation, your challenges, and what success looks like for you. No assumptions.",
              },
              {
                step: "02",
                title: "Requirements & Planning",
                desc: "We validate and document exactly what needs to be built, why, and in what order — so everyone is aligned before development begins.",
              },
              {
                step: "03",
                title: "UX/UI Design",
                desc: "We design interfaces that your team will actually find easy to use, not just beautiful screens. You review and provide feedback before any code is written.",
              },
              {
                step: "04",
                title: "Development",
                desc: "We build in iterative cycles with regular demonstrations so you can see progress, test features, and give feedback along the way.",
              },
              {
                step: "05",
                title: "Quality Assurance",
                desc: "We test everything thoroughly — functional, performance, and usability. Bugs are caught before they reach your users, not after.",
              },
              {
                step: "06",
                title: "Deployment",
                desc: "We handle the launch, including data migration, training, and go-live support so the transition is seamless for your team.",
              },
              {
                step: "07",
                title: "Support & Improvement",
                desc: "We stay on after launch to maintain, improve, and support your platform as it grows. Our relationship doesn't end at go-live.",
              },
            ].map((item, index) => (
              <div
                key={item.step}
                className="relative flex items-start gap-5 rounded-[16px] border border-[#E4E4E4] bg-white px-6 py-5 max-[760px]:flex-col max-[760px]:gap-3 max-[760px]:px-4 max-[760px]:py-4"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#F0F7FF] font-title text-[16px] font-semibold text-[#2776EA]">
                  {item.step}
                </div>
                <div className="flex-1">
                  <h3 className="font-title text-[18px] font-medium leading-tight text-[#212121] max-[760px]:text-[16px]">
                    {item.title}
                  </h3>
                  <p className="mt-1 max-w-[640px] font-description text-[14px] font-light leading-[1.55] text-[#919191]">
                    {item.desc}
                  </p>
                </div>
                {index < 6 && (
                  <div className="absolute bottom-0 left-[33px] h-[48px] w-px bg-[#D9D9D9] max-[760px]:hidden" />
                )}
              </div>
            ))}
          </div>

          <Link to="/contact">
            <Button className="mx-auto mt-10 flex w-fit rounded-full bg-[#2776EA] px-10 py-3 font-title text-[16px] font-semibold text-white shadow-[0_10px_25px_-5px_rgba(39,118,234,0.4)] transition-all hover:scale-[1.02] hover:shadow-[0_15px_30px_-5px_rgba(39,118,234,0.5)] active:scale-[0.98]">
              Ready to Build? Let's Talk
            </Button>
          </Link>
        </div>
      </section>

      <section className="bg-white pb-16 pt-20">
        <div className="mx-auto max-w-[1360px] px-5">
          <div className="text-center">
            <Badge className="inline-flex rounded-full border border-[#B8B8B8] bg-white px-3 py-1 font-description text-[10px] font-normal text-[#9C9C9C]">
              Products
            </Badge>
            <h2 className="mt-3 font-title text-[42px] font-semibold leading-none text-[#212121] max-[700px]:text-[32px]">
              Our Products &amp; Projects
            </h2>
            <p className="mx-auto mt-2 max-w-[640px] font-description text-[12px] font-light leading-[1.4] text-[#919191]">
              We don't just build software for clients. We also research,
              develop and validate our own digital products, giving us
              first-hand experience of turning ideas into practical solutions.
            </p>
          </div>

          <div className="mx-auto mt-8 grid max-w-[1280px] grid-cols-3 gap-3 max-[760px]:grid-cols-1">
            {PRODUCTS.map((product) => {
              const ctaClassName =
                "mt-auto inline-flex w-fit items-center gap-2 rounded-full bg-[#212121] px-5 py-[6px] font-title text-[10px] font-medium text-white hover:bg-black transition-colors";

              return (
                <Card
                  key={product.name}
                  className="flex min-h-[220px] flex-col rounded-[20px] bg-[#FAFAFA] px-5 pb-5 pt-5 max-[980px]:min-h-[200px]"
                >
                  <p className="font-description text-[10px] font-normal leading-[1.4] text-[#919191]">
                    {product.status}
                  </p>
                  <h3 className="mt-3 font-title text-[22px] font-medium leading-[1.1] text-[#212121]">
                    {product.name}
                  </h3>
                  <p className="mt-2 mb-6 font-description text-[12px] font-light leading-[1.45] text-[#919191]">
                    {product.description}
                  </p>
                  {product.external ? (
                    <a
                      href={product.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={ctaClassName}
                    >
                      {product.cta}
                      <span aria-hidden="true">ΓåÆ</span>
                    </a>
                  ) : (
                    <Link to={product.href} className={ctaClassName}>
                      {product.cta}
                      <span aria-hidden="true">ΓåÆ</span>
                    </Link>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-white pb-16 pt-20">
        <div className="mx-auto grid max-w-[1360px] grid-cols-2 items-center gap-12 px-5 max-[980px]:grid-cols-1">
          <div className="max-w-[620px] max-[980px]:order-2 max-[980px]:max-w-[760px] max-[980px]:text-center">
            <p className="font-description text-[13px] font-light leading-[1.5] text-[#919191]">
              Bao Technologies is a technology company specialising in the
              design and development of B2B digital solutions and internal
              projects.
            </p>
            <p className="mt-6 font-description text-[13px] font-light leading-[1.5] text-[#919191]">
              We support businesses, startups, and organisations in creating
              innovative, high-performing, and scalable digital products. From
              strategic thinking to production deployment, we combine technical
              expertise, user-centred design, and business understanding to
              deliver high-value solutions.
            </p>
          </div>

          <div className="justify-self-end text-right max-[980px]:order-1 max-[980px]:justify-self-center max-[980px]:text-center">
            <Badge className="inline-flex rounded-full border border-[#B8B8B8] bg-white px-3 py-1 font-description text-[10px] font-normal text-[#919191]">
              About Us
            </Badge>
            <h2 className="mt-4 font-title text-[36px] font-medium leading-[1.1] text-[#212121] max-[1100px]:text-[33px] max-[700px]:text-[28px]">
              About Bao
              <br />
              Technologies
            </h2>
          </div>
        </div>
      </section>

      <section className="bg-white pb-16 pt-20">
        <div className="mx-auto max-w-[1360px] px-5">
          <div className="grid grid-cols-2 items-start gap-12 max-[980px]:grid-cols-1">
            <div className="max-w-[630px] max-[980px]:max-w-[760px] max-[980px]:justify-self-center max-[980px]:text-center">
              <Badge className="inline-flex rounded-full border border-[#B8B8B8] bg-white px-3 py-1 font-description text-[10px] font-normal text-[#919191]">
                Our Mission
              </Badge>
              <h2 className="mt-4 font-title text-[36px] font-medium leading-[1.1] text-[#212121] max-[1100px]:text-[33px] max-[700px]:text-[28px]">
                We support businesses in
                <br />
                designing and optimising
                <br />
                scalable tech{" "}
                <span className="text-[#919191]">solutions based</span>
                <br />
                <span className="text-[#919191]">on their needs.</span>
              </h2>
            </div>

            <div className="max-w-[620px] justify-self-end max-[980px]:max-w-[760px] max-[980px]:justify-self-center max-[980px]:text-center">
              <p className="font-description text-[13px] font-light leading-[1.5] text-[#919191]">
                Our mission is to support B2B organizations in transforming
                their ideas into concrete, effective digital solutions that meet
                their business challenges. We help our partners to structure,
                secure, and manage their technology projects methodically, by
                designing bespoke tools that enhance operational efficiency and
                support growth.
              </p>
              <p className="mt-6 font-description text-[13px] font-light leading-[1.5] text-[#919191]">
                From strategic thinking to technical implementation, we engage
                at every stage of the project with a pragmatic, collaborative
                approach focused on sustainable results.
              </p>
            </div>
          </div>

          <img
            src={missionImage}
            alt="Team collaborating on digital solutions"
            className="mt-10 h-[250px] w-full rounded-[30px] object-cover"
          />
        </div>
      </section>

      <section className="bg-white pb-16 pt-16">
        <div className="mx-auto grid max-w-[1360px] grid-cols-2 items-center gap-12 px-5 max-[760px]:grid-cols-1">
          <div className="rounded-[30px] bg-[#FAFAFA] px-6 py-6">
            <p className="font-description text-[13px] font-light leading-[1.55] text-[#919191]">
              Managing a business in Cameroon, across Africa, and beyond is not
              for amateurs. Between manual processes and international tools
              that are ill-suited to our local context, growth quickly becomes a
              headache.
            </p>
            <p className="mt-7 font-description text-[13px] font-light leading-[1.55] text-[#919191]">
              Bao Technologies aims to be a facilitator and a driver of
              innovation and growth for African businesses and those elsewhere.
              We empower ourselves to provide our partners with everything they
              need to compete and rank among the best in their sectors.
            </p>
          </div>

          <div className="justify-self-end text-right max-[760px]:justify-self-center max-[760px]:text-center">
            <Badge className="mt-1 inline-flex rounded-full border border-[#B8B8B8] bg-white px-2.5 py-[3px] font-description text-[9px] font-normal text-[#919191]">
              Our Vision
            </Badge>
            <h2 className="mt-4 font-title text-[34px] font-medium leading-[1.1] text-[#212121] max-[1100px]:text-[30px] max-[700px]:text-[26px]">
              <span className="text-[#919191]">" Finally, a partner who</span>
              <br />
              speaks your language
              <br />
              and understands your
              <br />
              challenges "
            </h2>
          </div>
        </div>
      </section>

      <section className="bg-white pb-16 pt-16">
        <div className="mx-auto max-w-[1360px] px-5">
          <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-end">
            <div className="max-w-[520px]">
              <Badge className="inline-flex rounded-full border border-[#B8B8B8] bg-white px-3 py-1 font-description text-[10px] font-normal text-[#9C9C9C]">
                Why Us
              </Badge>
              <h2 className="mt-3 font-title text-[36px] font-medium leading-[1.1] text-[#212121] max-[1100px]:text-[32px] max-[700px]:text-[28px]">
                Why Bao Technologies?
              </h2>
            </div>
            <a
              href="https://www.youtube.com/watch?v=aqz-KE-bpKQ"
              target="_blank"
              rel="noopener noreferrer"
              className="relative block w-full max-w-[420px] overflow-hidden rounded-[24px] bg-[#00000033] lg:w-[420px]"
            >
              <img
                src={whyImage}
                alt="Team at work"
                className="h-[200px] w-full object-cover"
              />
              <span className="absolute left-1/2 top-1/2 inline-flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-[10px] bg-[#FF0000]">
                <svg
                  viewBox="0 0 24 24"
                  className="h-4 w-4 fill-white"
                  aria-hidden="true"
                >
                  <path d="M8 6.5V17.5L18 12L8 6.5Z" />
                </svg>
              </span>
            </a>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {WHY_BAO_ITEMS.map((item) => (
              <Card
                key={item.title}
                className="rounded-[24px] bg-[#FAFAFA] px-5 py-5"
              >
                <h3 className="font-title text-[16px] font-medium leading-[1.25] text-[#212121] md:text-[17px]">
                  {item.title}
                </h3>
                <p className="mt-2 font-description text-[12px] font-light leading-[1.5] text-[#919191] md:text-[13px]">
                  {item.description}
                </p>
              </Card>
            ))}
          </div>

          <div className="mt-10 flex justify-center">
            <Link to="/contact">
              <Button className="rounded-[8px] bg-[#2776EA] px-6 py-2.5 font-description text-[13px] font-medium text-white hover:bg-blue-600 transition-colors">
                Start a Conversation
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* <section className="bg-[#F8FAFE] pb-0 pt-14">
        <div className="mx-auto max-w-[1120px] px-5">
          <div className="relative overflow-visible rounded-[30px] px-5 pb-9 pt-6 max-[1100px]:px-4">
            <p className="max-w-[360px] font-description text-[22px] font-medium leading-none text-[#919191]/[0.06]">
              Testimonials
            </p>

            <div className="grid grid-cols-2 items-start gap-8 max-[980px]:grid-cols-1">
              <p className="-translate-x-32 max-w-[390px] font-description text-[13px] font-light leading-[1.5] text-[#919191] max-[980px]:order-2 max-[980px]:mx-auto max-[980px]:max-w-[640px] max-[980px]:translate-x-0 max-[980px]:text-center">
                At Bao Technologies, we deliver exceptional results and exceed
                client expectations. Our commitment to quality and satisfaction
                sets us apart. See what our clients say about their experiences.
              </p>

              <h2 className="-mt-3 translate-x-32 justify-self-end text-right font-title text-[36px] font-medium leading-[1.08] text-[#212121] max-[1200px]:text-[33px] max-[700px]:text-[28px] max-[980px]:order-1 max-[980px]:translate-x-0 max-[980px]:justify-self-center max-[980px]:text-center">
                What they say about
                <br />
                Bao Technologies
              </h2>
            </div>

            <p className="pointer-events-none absolute left-1/2 top-[155px] -translate-x-1/2 whitespace-nowrap font-title text-[178px] font-medium leading-none text-[#919191]/[0.05] max-[1200px]:top-[145px] max-[1200px]:text-[142px] max-[700px]:top-[134px] max-[700px]:text-[96px]">
              Testimonials
            </p>

            <div className="relative ml-auto mt-28 flex w-fit translate-x-60 items-end justify-end gap-4 max-[700px]:translate-x-0 max-[700px]:ml-0 max-[700px]:w-full max-[700px]:justify-start">
              <div className="mb-2 flex flex-col gap-3">
                <TestimonialNavButton direction="prev" />
                <TestimonialNavButton direction="next" />
              </div>

              <div className="w-fit overflow-visible max-[700px]:w-full">
                <div className="flex gap-4 max-[700px]:grid max-[700px]:grid-cols-1">
                  {TESTIMONIAL_ITEMS.map((item, index) => (
                    <Card
                      key={`${item.name}-${index}`}
                      className="w-[258px] shrink-0 rounded-[24px] border border-[#F2F2F2] bg-white px-4 py-4 shadow-[1px_2px_30px_0_rgba(0,0,0,0.04)] max-[700px]:w-full"
                    >
                      <p className="min-h-[74px] font-description text-[12px] font-light leading-[1.5] text-[#919191]">
                        {item.quote}
                      </p>
                      <div className="mt-4 flex items-center gap-2.5">
                        <span
                          aria-hidden="true"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-full"
                          style={{ backgroundColor: item.avatarColor }}
                        >
                          <svg
                            viewBox="0 0 24 24"
                            className="h-4 w-4 fill-[#7A7A7A]"
                          >
                            <path d="M12 12a4 4 0 1 0-4-4a4 4 0 0 0 4 4Zm0 2c-3.31 0-6 1.79-6 4v1h12v-1c0-2.21-2.69-4-6-4Z" />
                          </svg>
                        </span>
                        <div>
                          <p className="font-title text-[13px] font-medium leading-none text-[#212121]">
                            {item.name}
                          </p>
                          <p className="mt-1 font-description text-[10px] font-light leading-none text-[#919191]">
                            {item.role}
                          </p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div
            id="contact"
            className="relative mx-auto mb-[-510px] w-full max-w-[900px] rounded-[30px_30px_0_0] border-l border-r border-t border-[#E1E1E1] bg-white pb-5 pt-0 shadow-[1px_2px_30px_0_rgba(0,0,0,0.04)] max-[700px]:mb-[-430px] max-[700px]:max-w-[96%] scroll-mt-24"
          >
            <div className="h-9 w-full rounded-[30px_30px_0_0] border-b border-[#E3E3E3] bg-[#F7F7F7] px-6 pt-3.5">
              <div className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-[#D6D6D6]" />
                <span className="h-2 w-2 rounded-full bg-[#D6D6D6]" />
                <span className="h-2 w-2 rounded-full bg-[#D6D6D6]" />
              </div>
            </div>

            <div className="mt-5 grid grid-cols-[0.85fr_1.2fr_0.85fr] gap-5 px-4 max-[700px]:grid-cols-1">
              <div className="relative pt-2 max-[700px]:hidden">
                <div className="space-y-4">
                  <span className="block h-1.5 w-[50%] rounded-full bg-[#F4F4F4]" />
                  <span className="block h-1.5 w-[30%] rounded-full bg-[#F4F4F4]" />
                  <span className="block h-1.5 w-[44%] rounded-full bg-[#F4F4F4]" />
                  <span className="block h-1.5 w-[24%] rounded-full bg-[#F4F4F4]" />
                  <span className="block h-1.5 w-[40%] rounded-full bg-[#F4F4F4]" />
                  <span className="block h-1.5 w-[26%] rounded-full bg-[#F4F4F4]" />
                  <span className="block h-1.5 w-[46%] rounded-full bg-[#F4F4F4]" />
                </div>
                <div className="pointer-events-none absolute left-[-58px] top-[18px] flex flex-col items-start gap-[68px]">
                  <RoleBadge
                    label="Project Manager"
                    labelBg="#F1EAFE"
                    labelTextColor="#7130F0"
                    bubbleColor="#7130F0"
                    side="left"
                    className="-translate-x-6"
                  />
                  <RoleBadge
                    label="Software Engineer"
                    labelBg="#ECFBF1"
                    labelTextColor="#2F9A52"
                    bubbleColor="#2F9A52"
                    side="left"
                    className="mt-10"
                  />
                </div>
              </div>

              <div className="mx-auto w-full max-w-[360px] max-[700px]:max-w-[92%]">
                <div className="flex justify-center">
                  <Badge className="inline-flex rounded-full border border-[#E4E4E4] bg-white px-3 py-1 font-description text-[10px] font-normal text-[#B0B0B0]">
                    Get in touch
                  </Badge>
                </div>
                <h3 className="mt-3 text-center font-title text-[30px] font-medium leading-[1.08] text-[#212121] max-[700px]:text-[26px]">
                  Let&apos;s start a new journey
                  <br />
                  <span className="font-title text-[#2776EA]">together</span>
                </h3>
                <p className="mx-auto mt-2.5 max-w-[340px] text-center font-description text-[11px] font-light leading-[1.45] text-[#919191]">
                  Reach out to us today to explore how our technology solutions
                  can drive your business forward. Our team of experts is ready
                  to assist you.
                </p>

                <div className="mt-3.5 flex items-center gap-3">
                  <Button
                    type="button"
                    onClick={() => setContactAudience("personal")}
                    className={`rounded-full px-3 py-[5px] font-title text-[10px] font-medium ${
                      contactAudience === "personal"
                        ? "bg-[#F7FAFF] text-[#5291EE]"
                        : "text-[#B0B0B0]"
                    }`}
                  >
                    For you (Personal)
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setContactAudience("company")}
                    className={`rounded-full px-3 py-[5px] font-title text-[10px] font-medium ${
                      contactAudience === "company"
                        ? "bg-[#F7FAFF] text-[#5291EE]"
                        : "text-[#B0B0B0]"
                    }`}
                  >
                    For your company
                  </Button>
                </div>

                <form
                  className="mt-3.5 space-y-2.5"
                  onSubmit={onSubmit}
                  noValidate
                >
                  {isSubmitted && (
                    <div className="rounded-[8px] border border-green-200 bg-green-50 px-3 py-2 font-description text-[10px] text-green-700">
                      Thank you! Your message has been sent.
                    </div>
                  )}
                  {contactError && (
                    <div className="rounded-[8px] border border-red-200 bg-red-50 px-3 py-2 font-description text-[10px] text-red-700">
                      {contactError}
                    </div>
                  )}
                  <FormField
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    label="Full Name"
                    placeholder="Enter your full name"
                    error={errors.name}
                  />
                  <FormField
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    label="E-mail address"
                    type="email"
                    placeholder="Enter your email address"
                    error={errors.email}
                  />
                  <FormField
                    name="subject"
                    value={form.subject}
                    onChange={onChange}
                    label="Subject"
                    placeholder="Enter your subject line"
                    error={errors.subject}
                  />
                  <FormField
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    label="Message"
                    placeholder="Enter your message"
                    multiline
                    error={errors.message}
                  />
                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="rounded-[8px] bg-[#2776EA] px-7 py-[6px] font-title text-[11px] font-medium text-white disabled:cursor-not-allowed disabled:bg-[#E4E4E4]"
                    >
                      {isSubmitting ? "Sending..." : "Submit"}
                    </Button>
                  </div>
                </form>
              </div>

              <div className="relative pt-2 max-[700px]:hidden">
                <div className="flex justify-end gap-3">
                  <span className="block h-1.5 w-[28%] rounded-full bg-[#F7F7F7]" />
                  <span className="block h-1.5 w-[22%] rounded-full bg-[#F7F7F7]" />
                </div>
                <div className="pointer-events-none absolute right-[-30px] top-[8px] flex flex-col items-end gap-[68px]">
                  <RoleBadge
                    label="Design Engineer"
                    labelBg="#E9F1FD"
                    labelTextColor="#2776EA"
                    bubbleColor="#2776EA"
                    side="right"
                    className="translate-x-10"
                  />
                  <RoleBadge
                    label="Marketing Strategist"
                    labelBg="#FFF5E6"
                    labelTextColor="#B56B03"
                    bubbleColor="#B56B03"
                    side="right"
                    className="mt-10 translate-x-2"
                  />
                </div>
              </div>
            </div>
            <div className="pointer-events-none absolute bottom-0 left-0 h-20 w-full bg-gradient-to-b from-transparent to-white" />

            <div className="pointer-events-none hidden max-[700px]:block">
              <RoleBadge
                label="Project Manager"
                labelBg="#F1EAFE"
                labelTextColor="#7130F0"
                bubbleColor="#7130F0"
                side="left"
                className="absolute left-[-58px] top-[92px] scale-[0.62]"
              />
              <RoleBadge
                label="Software Engineer"
                labelBg="#ECFBF1"
                labelTextColor="#2F9A52"
                bubbleColor="#2F9A52"
                side="left"
                className="absolute left-[-58px] top-[236px] scale-[0.62]"
              />
              <RoleBadge
                label="Design Engineer"
                labelBg="#E9F1FD"
                labelTextColor="#2776EA"
                bubbleColor="#2776EA"
                side="right"
                className="absolute right-[-58px] top-[92px] scale-[0.62]"
              />
              <RoleBadge
                label="Marketing Strategist"
                labelBg="#FFF5E6"
                labelTextColor="#B56B03"
                bubbleColor="#B56B03"
                side="right"
                className="absolute right-[-58px] top-[236px] scale-[0.62]"
              />
            </div>
          </div>
        </div>
      </section> */}

      {/* <div className="h-[620px] bg-white" /> */}

      {/* <section className="bg-white pb-16 pt-16">
        <div className="mx-auto max-w-[1280px] px-5">
          <div className="grid min-h-[165px] grid-cols-[1.24fr_0.76fr] overflow-hidden rounded-[38px] bg-[#7130F0] max-[700px]:grid-cols-1">
            <div className="flex items-center px-7 py-5 max-[980px]:px-6 max-[980px]:pb-3">
              <div className="max-w-[420px]">
                <h2 className="translate-x-3 font-title text-[34px] font-medium leading-[1.08] text-white max-[1200px]:text-[30px] max-[700px]:text-[26px]">
                  Ready to learn or level up
                  <br />
                  your tech skills ?
                </h2>
                <p className="mt-2.5 max-w-[390px] translate-x-3 font-description text-[14px] font-light leading-[1.4] text-[#F1EAFE] max-[1200px]:text-[13px] max-[700px]:text-[12px]">
                  Bao Academy is your gateway to mastering the latest tech. Dive
                  into expert-led courses and unlock your potential in the
                  digital world.
                </p>
                <Link
                  to="/contact?subject=Book%20a%20Consultation"
                  className="mt-4 inline-flex translate-x-3 items-center gap-2 rounded-full bg-[#FFFFFF33] px-5 py-[7px] font-title text-[12px] font-medium text-white"
                >
                  Go to Bao Academy
                  <span aria-hidden="true">ΓåÆ</span>
                </Link>
              </div>
            </div>

            <div className="relative flex min-h-[165px] items-end justify-end">
              <span className="pointer-events-none absolute left-[-20px] top-1/2 h-[560px] w-[560px] -translate-y-1/2 rounded-full border border-[#FFFFFF40]" />
              <span className="pointer-events-none absolute left-[-6px] top-[45%] h-[640px] w-[560px] -translate-y-1/2 rotate-[8deg] rounded-full border border-[#FFFFFF2E]" />
              <img
                src={skillImage}
                alt="Person wearing a VR headset"
                className="h-[80%] w-[80%] -translate-x-16 -translate-y-7 scale-[1.34] mix-blend-hard-light object-contain object-right-bottom max-[700px]:h-full max-[700px]:w-full"
              />
            </div>
          </div>
        </div>
      </section> */}

      {showBlogSection && (
        <section className="bg-white pb-40 pt-20">
          <div className="mx-auto max-w-[1360px] px-5">
            <div className="text-center">
              <Badge className="inline-flex rounded-full border border-[#DEDEDE] bg-white px-3 py-1 font-description text-[10px] font-normal text-[#A1A1A1]">
                Our Blogs
              </Badge>
              <h2 className="mt-4 font-title text-[38px] font-medium leading-[1.08] text-[#212121] max-[1100px]:text-[34px] max-[700px]:text-[28px]">
                Articles that may interest you
              </h2>
              <p className="mx-auto mt-3 max-w-[640px] font-description text-[12px] font-light leading-[1.45] text-[#919191]">
                Insights on building better software, navigating digital growth,
                and what&apos;s working for B2B businesses in Africa.
              </p>
            </div>

            <div className="mx-auto mt-10 grid max-w-[1100px] grid-cols-2 gap-8 max-[700px]:grid-cols-1">
              {featuredArticles.map((item) => (
                <Card key={item.slug || item.title} className="bg-white">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="h-[220px] w-full rounded-[18px] object-cover"
                  />
                  <div className="mt-3 flex items-center justify-between">
                    <p className="font-title text-[12px] font-normal text-[#919191]">
                      {item.category}
                      <span className="mx-2">┬╖</span>
                      {item.date}
                    </p>
                    <p className="font-title text-[12px] font-normal text-[#919191]">
                      {item.author}
                    </p>
                  </div>
                  <h3 className="mt-2 font-title text-[22px] font-medium leading-[1.15] text-[#212121] max-[1100px]:text-[20px] max-[700px]:text-[18px]">
                    {item.title}
                  </h3>
                  {item.description ? (
                    <p className="mt-2.5 max-w-[95%] font-description text-[13px] font-light leading-[1.45] text-[#919191]">
                      {item.description}
                    </p>
                  ) : null}
                  <Link
                    to={`/blog/${item.slug}`}
                    className="mt-4 inline-flex items-center gap-2 font-title text-[14px] font-semibold text-[#212121]"
                  >
                    Read the article
                    <span aria-hidden="true">Γåù</span>
                  </Link>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-[#F8FAFE] py-16">
        <div className="mx-auto max-w-[800px] px-5 text-center">
          <h2 className="font-title text-[28px] font-medium leading-[1.1] text-[#212121] md:text-[34px]">
            Let&apos;s Build Something That Matters.
          </h2>
          <p className="mx-auto mt-3 max-w-[560px] font-description text-[13px] font-light leading-[1.55] text-[#919191] md:text-[14px]">
            Whether you're improving an existing process, launching a new
            service, or exploring an early-stage idea, we'd love to help you
            turn it into something practical.
          </p>
          <Link to="/contact" className="mt-6 inline-block">
            <Button className="rounded-[8px] bg-[#2776EA] px-6 py-2.5 font-description text-[13px] font-medium text-white hover:bg-blue-600 transition-colors">
              Start a Conversation
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
